"use strict";

import { ObjectCreator } from "../objectCreator.js";
import { Application } from "../../main/application.js";
import { Screen } from "../../systems/core/screen.js";
import { AssetImporter } from "../../systems/core/assetImporter.js";
import { TransformHandler } from "../transformHandler.js";

export class Spine3D extends PIXI.projection.Spine3d {
    constructor(config) {
        // Create the main Object
        let spineData = undefined;

        if (config.spineData !== undefined) {
            if (typeof config.spineData === "string") {
                spineData = AssetImporter.resourcesFind(config.spineData).spineData;
            } else {
                spineData = config.spineData;
            }
        } else {
            console.error("Spine can't exist without spine data.");
        }
        
        super(spineData);
        this.__objectType = "GameObject";

        this.name = config.name;

        // Create Parenting
        ObjectCreator.createParenting(this, config);

        // Create Anchoring
        ObjectCreator.createAnchoring(this, false, true, config);

        // Set Scale
        ObjectCreator.setScale(this, config);

        // Set Position
        ObjectCreator.setPosition(this, true, config);
        this._refreshPosition();
        
        // Set Rotation Angle
        ObjectCreator.setRotationAngle(this, config);
        // Set Visibility
        ObjectCreator.setVisibility(this, config);
        // Set Renderability
        ObjectCreator.setRenderability(this, config);

        // Set 3D Rotations
        ObjectCreator.set3DRotations(this, config);
        // Set 3D Functions
        ObjectCreator.set3DFunctions(this);

        ObjectCreator.setResolutionListeners(this, false, config);
        ObjectCreator.setOrientationListeners(this, false, config);

        ObjectCreator.setDisposeCallbacks(this);

        this.animations = typeof config.animations === "string" ?
            [ config.animations ] : config.animations;
        this.currentAnimation = 0;

        this.events = typeof config.events === "string" ?
            [ config.events ] : config.events;
        this.onEventTriggered = config.onEventTriggered;

        if (this.onEventTriggered !== undefined) {
            this.state.addListener({ event: (entry,event) => {
                if (this.events === undefined) return;
                if (this.events.indexOf(event.data) == -1) return;
                this.onEventTriggered?.(event.data);
            }});
        }
        this.state.addListener({ complete: (trackEntry) => this.onEnded?.(trackEntry) });
        
        this.skins = [];
        this.skeleton.data.skins.forEach(skin => this.skins.push(skin.name));
        this._currentSkin = 0;

        this.timeScale = 1;

        if (config.worldPosition !== undefined) {
            this.setWorldPosition(config.worldPosition);
        }

        this._externalBounds = config.externalBounds;

        config.onInitialize?.bind(this)(config);
    }

    get forward() {
        return Vector3.getObjectForward(this.getRotation());
    }

    dispose() {
        this._onDispose.dispatch();
        this.renderable = false;
        this.parentContainer?.removeChild(this);
        this.destroy();
    }

    // SetParent
    setParent(newParent) {
        this.parentContainer = newParent;
        this.parentContainer.addChild(this);
        this.onResolutionChanged();
    }

    // Get - Set X/Y
    getX() {
        return this.position3d.x;
    }
    getY() {
        return this.position3d.y;
    }
    getZ() {
        return this.position3d.z;
    }

    setX(x) {
        this.anchoring.margin.x = x;
        this._refreshPosition();
    }
    setY(y) {
        this.anchoring.margin.y = y;
        this._refreshPosition();
    }
    setZ(z) {
        this.anchoring.margin.z = z;
        this._refreshPosition();
    }

    // Get - Set Position
    getPosition() {
        return { x: this.position3d.x, y: this.position3d.y, z: this.position3d.z };
    }
    setPosition(x,y,z) {
        this.anchoring.margin.x = x.x ?? x;
        this.anchoring.margin.y = x.y ?? y;
        this.anchoring.margin.z = x.z ?? z ?? 0;
        this._refreshPosition();
    }

    getRealWorldPosition() {
        this.parentContainer.updateTransform();
        return {...this.parentContainer.toGlobal(this.getPosition()), z: this.getZ()};
    }
    getWorldPosition() {
        let position = this.getRealWorldPosition();
        position.x -= Application.main.worldCenterX;
        position.y -= Application.main.worldCenterY;
        return position;
    }
    setRealWorldPosition(x,y,z) {
        let tempZ = x.z ?? z ?? 0;
        this._tPosition.x = x.x ?? x;
        this._tPosition.y = x.y ?? y;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        this._tPosition.z = tempZ - this.parentContainer.getWorldPosition().z;
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;
        this.anchoring.margin.z = this._tPosition.z;

        this._refreshPosition();
    }
    setWorldPosition(x,y,z) {
        let tempZ = x.z ?? z ?? 0;
        this._tPosition.x = (x.x ?? x) + Application.main.worldCenterX;
        this._tPosition.y = (x.y ?? y) + Application.main.worldCenterY;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        this._tPosition.z = tempZ - this.parentContainer.getWorldPosition().z;
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;
        this.anchoring.margin.z = this._tPosition.z;

        this._refreshPosition();
    }
    
    getWorldPositionScale() {
        return this.worldTransform.mat4[15] / Screen.main.stageScale;
    }
    transformLocalToWorld(value) {
        return value / this.getWorldPositionScale();
    }
    transformLocalToWorldPosition(position) {
        position.x = this.transformLocalToWorld(position.x);
        position.y = this.transformLocalToWorld(position.y);
        if (position.z !== undefined) {
            position.z = this.transformLocalToWorld(position.z);
        }
    }

    _refreshPosition() {
        if (this.anchoring.anchorBounds !== undefined) {
            TransformHandler.updateAnchor3D(this);
        } else {
            TransformHandler.updateMargin3D(this);
        }
    }

    setRotationX(x) {
        this.eulerAngles.x = x;
        this.euler.x = Math.degreesToRadians(x);
    }
    setRotationY(y) {
        this.eulerAngles.y = y;
        this.euler.y = Math.degreesToRadians(y);
    }
    setRotationZ(z) {
        this.eulerAngles.z = z;
        this.euler.z = Math.degreesToRadians(z);
    }
    setRotation(x,y,z) {
        if (x.x !== undefined) {
            z = x.z;
            y = x.y;
            x = x.x;
        }
        this.setRotationX(x);
        this.setRotationY(y);
        this.setRotationZ(z);
    }

    getRotationX() {
        return this.eulerAngles.x;
    }
    getRotationY() {
        return this.eulerAngles.y;
    }
    getRotationZ() {
        return this.eulerAngles.z;
    }
    getRotation() {
        return {...this.eulerAngles};
    }

    setAnchorX(x) {
        this.anchoring.anchor.x = x;
        this._refreshPosition();
    }
    setAnchorY(y) {
        this.anchoring.anchor.y = y;
        this._refreshPosition();
    }
    setAnchor(x,y) {
        this.anchoring.anchor.x = x.x ?? x;
        this.anchoring.anchor.y = x.y ?? y ?? x;
        this._refreshPosition();
    }

    setAnchorBounds(bounds) {
        this.anchoring.anchorBounds = bounds;
        this._refreshPosition();
    }

    // Get - Set Scale
    getScale() {
        return this.scale;
    }
    setScale(x,y) {
        this.scale.set(x, y ?? x);
    }
    setScaleX(x) {
        this.scale.set(x, this.scale.y);
    }
    setScaleY(y) {
        this.scale.set(this.scale.x, y);
    }

    // Get - Width/Height/Size
    get width() {
        return this.getLocalBounds().width;
    }
    get height() {
        return this.getLocalBounds().height;
    }
    getSize() {
        return { width: this.width, height: this.height };
    }
    getRealWorldSize() {
        return {
            width: this.scaledWidth * Screen.main.stageScale,
            height: this.scaledHeight * Screen.main.stageScale
        };
    }

    get scaledWidth() {
        return this.width * this.scale.x;
    }
    get scaledHeight() {
        return this.height * this.scale.y;
    }
    get unscaledWidth() {
        return this.width / this.scale.x;
    }
    get unscaledHeight() {
        return this.height / this.scale.y;
    }

    get lossyScaleX() {
        return this.scale.x * (this.parentContainer.lossyScaleX ?? 1);
    }
    get lossyScaleY() {
        return this.scale.y * (this.parentContainer.lossyScaleY ?? 1);
    }
    get lossyScale() {
        return this.lossyScaleY;
    }

    get boundX() {
        return this.getLocalBounds().width * this.scale.x;
    }
    get boundY() {
        return this.getLocalBounds().height * this.scale.y;
    }
    get bounds() {
        return { width: this.boundX, height: this.boundY };
    }
    get boundWithOffsetX() {
        return this.boundX + this.getLocalBounds().x * this.scale.x;
    }
    get boundWithOffsetY() {
        return this.boundY + this.getLocalBounds().y * this.scale.y;
    }
    get boundsWithOffset() {
        return { width: this.boundWithOffsetX, height: this.boundWithOffsetY };
    }

    get externalBounds() {
        return this._externalBounds ?? (this.parentContainer !== undefined ?
            this.parentContainer.externalBounds : { width: 1, height: 1 });
    }
    set externalBounds(value) {
        this._externalBounds = value;
    }
    get unscaledExternalBounds() {
        return Vector2.multiplySize(this.externalBounds, this.scale.y / this.lossyScale);
    }

    scaleValue(value) {
        return value * this.lossyScale * Screen.main.stageScale;
    }

    onResolutionChanged() {
        if (this.parentContainer === undefined) return;
        
        this._onResolutionWillChangeCallback?.();

        this._refreshPosition();

        this._onResolutionChangedCallback?.();
    }

    onOrientationChanged() {
        this._onOrientationChangedCallback?.();
    }

    play(index,loop,onEnded) {
        this.onEnded = loop ? undefined : onEnded;
        this.currentAnimation = index;
        let trackEntry = this.state.setAnimation(0, this.animations[index], loop);
        trackEntry.timeScale = this.timeScale;
        
        return trackEntry;
    }

    playFrom(index,delta,loop,onEnded) {
        if (delta === undefined) {
            delta = 0;
        }
        this.onEnded = loop ? undefined : onEnded;
        this.currentAnimation = index;
        let trackEntry = this.state.setAnimation(0, this.animations[index], loop);
        this.state.update(trackEntry.animationEnd * delta);
        trackEntry.timeScale = this.timeScale;
        
        return trackEntry;
    }

    playSingle(index,time) {
        this.playSingleFrame(index,time);
    }
    playSingleFrame(index,time) {
        if (time === undefined) {
            time = 0;
        }
        this.currentAnimation = index;
        let trackEntry = this.state.setAnimation(0, this.animations[index], false);
        this.state.update(trackEntry.animationEnd * time);
        trackEntry.timeScale = 0;

        return trackEntry;
    }

    stop() {
        this.state.setEmptyAnimation(0);
    }

    getDelta() {
        return this.state.tracks[0].animationDelta;
    }

    setSkinByName(skinName) {
        this.skeleton.setSkinByName(skinName);
        this._currentSkin = this.skins.indexOf(skinName);
    }
    setSkinByIndex(index) {
        this.skeleton.setSkinByName(this.skins[index]);
        this._currentSkin = index;
    }
}